import { PocProgressSpinner } from 'commons/components/components-wrapper/poc-progress-spinner.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { SelectOption, SelectPropsValue } from 'commons/components';
import { CustomField } from 'core/service/custom-fields/custom-field.type';
import { useCustomFields } from 'core/contexts/custom-fields/use-custom-fields.hook';
import { useTranslation } from 'core/i18n/i18n';
import { POCFieldAttribute } from 'modules/settings/pods/settings-integrations/service';
import { DataPointMappingHeaderComponent } from 'modules/settings/pods/settings-integrations/components';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { TrainingRegistrationMapping } from '../types/lms-training-registration-mapping.type';
import { DataPointMappingComponent } from './data-point-mapping.component';
import { DataPointSectionComponent } from './data-point-section.component';
import { useFetchPocFields } from './hooks/use-fetch-poc-fields.hook';
import { TrainingRegistrationMappingWrapper } from './training-registration-management.style';
import { withOptionalLabel } from 'commons/utils/optional-label.utils';

interface TrainingRegistrationManagementMappingsProps {
    headers: string[];
    mappings: TrainingRegistrationMapping[];
    fallback?: TrainingRegistrationMapping;
    disableEdit?: boolean;
    setMappings?: React.Dispatch<React.SetStateAction<TrainingRegistrationMapping[]>>;
    setFallback?: React.Dispatch<React.SetStateAction<TrainingRegistrationMapping>>;
    setPocFieldsPresent?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const TrainingRegistrationManagementMappings: React.FC<TrainingRegistrationManagementMappingsProps> = memo(
    (props) => {
        const {
            headers,
            mappings,
            fallback,
            disableEdit = false,
            setPocFieldsPresent,
            setMappings,
            setFallback,
        } = props;
        const { operatorsCustomFields } = useCustomFields();
        const { pocFields: pocFieldsResult, isFetching: loading } = useFetchPocFields();

        const { t } = useTranslation();

        const mapPocFields = (customFields: CustomField[], pocFieldsAttributes: POCFieldAttribute[]) =>
            pocFieldsAttributes.map((f) => ({
                value: f.attribute,
                text:
                    customFields.find((c: CustomField) => c.id === f.attribute)?.name ??
                    t(
                        `settings.integrations.active-directory.data-points.mapping.poc.fields.value.${f.attribute}`,
                        f.attribute
                    ),
            }));

        const headerOptions: SelectOption[] = useMemo(
            () => headers.map((header) => ({ value: header, text: header })),
            [headers]
        );

        const pocFields: SelectOption[] = useMemo(
            () => mapPocFields(operatorsCustomFields, pocFieldsResult),
            [operatorsCustomFields, pocFieldsResult]
        );

        useEffect(() => {
            if (!loading) {
                setPocFieldsPresent?.(true);
            }
        }, [loading, setPocFieldsPresent]);

        const handleOnChangeMapping = (
            index: number,
            field: string,
            value: string,
            entity: TrainingRegistrationMapping['targetEntity']
        ) => {
            const mappingsCopy = structuredClone(mappings);
            const newMapping = { ...mappingsCopy[index] };
            newMapping[field as keyof Omit<TrainingRegistrationMapping, 'targetEntity'>] = value;
            newMapping.targetEntity = entity;
            mappingsCopy.splice(index, 1, newMapping);

            setMappings?.(mappingsCopy);
        };

        const getAvailableOptions = useCallback(
            (mappingIndex: number) =>
                headerOptions.filter((header) =>
                    mappings.every((mapping, i) => mapping.lmsField !== header.value || i === mappingIndex)
                ),
            [mappings, headerOptions]
        );

        const renderMappingComponent = (mapping: TrainingRegistrationMapping, index: number) => (
            <DataPointMappingComponent
                key={`field-${mapping.targetEntity}-${mapping.pocField}`}
                inputName={`field-${mapping.targetEntity}`}
                inputValue={t(`settings.integrations.lms-api-integration.mapping.fields.${mapping.pocField}.value`)}
                mappingName={`data-points-${mapping.targetEntity}`}
                mappingPlaceholder={
                    t(`settings.integrations.lms-api-integration.mapping.fields.${mapping.pocField}.placeholder`) ??
                    undefined
                }
                mappingValue={mapping.lmsField}
                mappingOptions={getAvailableOptions(index)}
                disableMapping={disableEdit}
                showTooltip={disableEdit}
                tooltipText={t('settings.integrations.lms-api-integration.mapping.tooltip.disable-edit')}
                onChange={(field, value) =>
                    handleOnChangeMapping(
                        index,
                        field === `field-${mapping.targetEntity}` ? 'pocField' : 'lmsField',
                        value as string,
                        mapping.targetEntity
                    )
                }
            />
        );

        const handleFallbackChange = (field: string, value: SelectPropsValue) => {
            setFallback?.({
                ...fallback!,
                [field === 'poc-field-fallback' ? 'pocField' : 'lmsField']: value as string,
            });
        };

        const fallbackInputPlaceholder =
            fallback?.pocField && !pocFields.map((pf) => pf.value).includes(fallback?.pocField ?? '')
                ? t('settings.integrations.lms-api-integration.mapping.fields.fallback.placeholder.non-existing')
                : t('settings.integrations.lms-api-integration.mapping.fields.fallback.placeholder');
        return (
            <TrainingRegistrationMappingWrapper>
                {loading ? (
                    <div className="loading-mask">
                        <PocProgressSpinner size="xLarge" />
                    </div>
                ) : (
                    <>
                        <DataPointSectionComponent>
                            <DataPointMappingHeaderComponent
                                pocMappingHeader={t(
                                    'settings.integrations.lms-api-integration.mapping.fields.header.mandatory-fields'
                                )}
                                ldapMappingHeader={t(
                                    'settings.integrations.lms-api-integration.mapping.fields.header.data-points'
                                )}
                            />
                            {mappings.map((mapping, index) => renderMappingComponent(mapping, index))}
                        </DataPointSectionComponent>

                        <DataPointSectionComponent>
                            <DataPointMappingHeaderComponent
                                pocMappingHeader={withOptionalLabel(
                                    t,
                                    t('settings.integrations.lms-api-integration.mapping.fields.header.fallback')
                                )}
                                ldapMappingHeader={t(
                                    'settings.integrations.lms-api-integration.mapping.fields.header.data-points'
                                )}
                            />
                            <DataPointMappingComponent
                                inputOption="select"
                                inputName={`poc-field-fallback`}
                                inputValue={fallback?.pocField}
                                inputOptions={pocFields}
                                inputPlaceholder={disableEdit ? fallbackInputPlaceholder : undefined}
                                mappingName={'data-points-fallback'}
                                mappingPlaceholder={
                                    disableEdit
                                        ? t(
                                              'settings.integrations.lms-api-integration.mapping.fields.fallback.placeholder'
                                          )
                                        : undefined
                                }
                                mappingValue={fallback?.lmsField}
                                mappingOptions={headerOptions}
                                disableMapping={disableEdit}
                                showTooltip={disableEdit}
                                tooltipText={t(
                                    'settings.integrations.lms-api-integration.mapping.tooltip.disable-edit'
                                )}
                                onChange={handleFallbackChange}
                                mandatoryField={false}
                            />
                            {!disableEdit && (
                                <PocTypography variant="body1" style={{ marginTop: '20px' }}>
                                    {t('settings.integrations.lms-api-integration.mapping.fields.header.description')}
                                </PocTypography>
                            )}
                        </DataPointSectionComponent>
                    </>
                )}
            </TrainingRegistrationMappingWrapper>
        );
    }
);
